import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  UserPlus, Mail, Shield, ShieldCheck, Trash2, Copy, 
  CheckCircle, Clock, XCircle, Users, Crown 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function InviteManagement({ currentUser }) {
  const [invites, setInvites] = useState([]);
  const [users, setUsers] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('member');
  const [isInviting, setIsInviting] = useState(false);
  const [activeTab, setActiveTab] = useState('invites');

  useEffect(() => {
    if (currentUser?.is_admin) {
      fetchInvites();
      fetchUsers();
    }
  }, [currentUser]);

  const fetchInvites = async () => {
    try {
      const response = await axios.get(`${API}/invites`, { withCredentials: true });
      setInvites(response.data);
    } catch (error) {
      console.error('Error fetching invites:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    setIsInviting(true);
    try {
      const response = await axios.post(
        `${API}/invites`,
        { email: newEmail, role: newRole },
        { withCredentials: true }
      );
      
      toast.success(`Invite sent to ${newEmail}`);
      setNewEmail('');
      setNewRole('member');
      fetchInvites();
      
      // Show invite code for sharing
      const inviteCode = response.data.invite_code;
      toast.info(`Invite code: ${inviteCode}`, { duration: 10000 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    if (!window.confirm('Are you sure you want to revoke this invite?')) return;

    try {
      await axios.delete(`${API}/invites/${inviteId}`, { withCredentials: true });
      toast.success('Invite revoked');
      fetchInvites();
    } catch (error) {
      toast.error('Failed to revoke invite');
    }
  };

  const handleToggleAdmin = async (userId) => {
    try {
      const response = await axios.put(
        `${API}/users/${userId}/admin`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleRemoveUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to remove ${userName}? This cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      toast.success('User removed');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    }
  };

  const copyInviteLink = (inviteCode) => {
    const link = `${window.location.origin}/login?invite=${inviteCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied!');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-amber-500" />;
      case 'accepted':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'revoked':
        return <XCircle className="w-4 h-4 text-rose-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'accepted':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'revoked':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  if (!currentUser?.is_admin) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
        <Shield className="w-12 h-12 text-amber-500 mx-auto mb-3" />
        <p className="text-amber-700 font-medium">Admin Access Required</p>
        <p className="text-amber-600 text-sm mt-1">
          Only administrators can manage invites and users.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Invite Form */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center space-x-2">
          <UserPlus className="w-5 h-5 text-indigo-600" />
          <span>Invite New Member</span>
        </h3>
        
        <form onSubmit={handleInvite} className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="colleague@company.com"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 outline-none"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="w-full sm:w-auto px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 outline-none bg-white"
                data-testid="invite-role-select"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={isInviting}
              className="px-6 py-2.5 bg-indigo-700 text-white font-medium rounded-lg hover:bg-indigo-800 transition-colors disabled:opacity-50 flex items-center space-x-2"
              data-testid="send-invite-button"
            >
              {isInviting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Mail className="w-4 h-4" />
              )}
              <span>Send Invite</span>
            </button>
          </div>
        </form>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('invites')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'invites'
              ? 'border-indigo-600 text-indigo-700'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <span className="flex items-center space-x-2">
            <Mail className="w-4 h-4" />
            <span>Invites ({invites.filter(i => i.status === 'pending').length})</span>
          </span>
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'users'
              ? 'border-indigo-600 text-indigo-700'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <span className="flex items-center space-x-2">
            <Users className="w-4 h-4" />
            <span>Users ({users.length})</span>
          </span>
        </button>
      </div>

      {/* Invites List */}
      <AnimatePresence mode="wait">
        {activeTab === 'invites' && (
          <motion.div
            key="invites"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-white border border-slate-200 rounded-xl overflow-hidden"
          >
            {invites.length === 0 ? (
              <div className="p-8 text-center">
                <Mail className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No invites yet</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {invites.map((invite) => (
                  <div
                    key={invite.invite_id}
                    className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{invite.email}</p>
                        <p className="text-xs text-slate-500">
                          Invited by {invite.invited_by_name} • {invite.role}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border flex items-center space-x-1 ${getStatusColor(invite.status)}`}>
                        {getStatusIcon(invite.status)}
                        <span className="capitalize">{invite.status}</span>
                      </span>
                      {invite.status === 'pending' && (
                        <>
                          <button
                            onClick={() => copyInviteLink(invite.invite_code)}
                            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Copy invite link"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRevokeInvite(invite.invite_id)}
                            className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                            title="Revoke invite"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Users List */}
        {activeTab === 'users' && (
          <motion.div
            key="users"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-white border border-slate-200 rounded-xl overflow-hidden"
          >
            {users.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No users yet</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {users.map((user) => (
                  <div
                    key={user.user_id}
                    className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      {user.picture ? (
                        <img
                          src={user.picture}
                          alt={user.name}
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-indigo-700 font-semibold">
                            {user.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-slate-900 flex items-center space-x-2">
                          <span>{user.name}</span>
                          {user.is_admin && (
                            <Crown className="w-4 h-4 text-amber-500" title="Admin" />
                          )}
                          {user.user_id === currentUser.user_id && (
                            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                              You
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                    </div>
                    {user.user_id !== currentUser.user_id && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleToggleAdmin(user.user_id)}
                          className={`p-2 rounded-lg transition-colors ${
                            user.is_admin
                              ? 'text-amber-600 hover:bg-amber-50'
                              : 'text-slate-400 hover:text-indigo-600 hover:bg-indigo-50'
                          }`}
                          title={user.is_admin ? 'Remove admin' : 'Make admin'}
                        >
                          {user.is_admin ? (
                            <ShieldCheck className="w-4 h-4" />
                          ) : (
                            <Shield className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleRemoveUser(user.user_id, user.name)}
                          className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                          title="Remove user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default InviteManagement;
